/**
 * Estado global de la aplicación Clinical Graph Triage
 * Singleton pattern para gestión centralizada del estado
 */

import { 
  TriagePriority, 
  PatientGraph, 
  TopologyMode, 
  TopologyConfiguration,
  ClinicalEvent,
  DetectedPattern,
  DifferentialDiagnosis,
  AnatomicalSystem
} from '../types/medical';
import { MEDICAL_PHYSICS } from './config';
import * as THREE from 'three';

// ═══════════════════════════════════════════════════════════════════════════════
// INTERFACES DE ESTADO
// ═══════════════════════════════════════════════════════════════════════════════

export interface HandState {
  isPresent: boolean;
  isPinching: boolean;
  isGrabbing: boolean;
  pinchDistance: number;
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  landmarks?: Array<{x: number, y: number, z: number}>;
  /**
   * Posición 2D del dedo índice en coordenadas normalizadas 0..1, ya flipeada
   * horizontalmente (cámara frontal es espejo). Se usa para hacer raycast
   * igual que el mouse: más confiable que la posición 3D porque MediaPipe
   * no entrega Z confiable.
   */
  indexTipScreen: { x: number; y: number } | null;
  gesture: string | null;
  lastGestureTime: number;
}

export interface SimulationState {
  isPaused: boolean;
  timeScale: number;
  globalTime: number;
  patientTime: number;
  deltaTime: number;
  frameCount: number;
}

export interface InteractionState {
  hoveredNodeId: string | null;
  selectedNodeId: string | null;
  draggedNodeId: string | null;
  lastInteractionTime: number;
  isEmergencyMode: boolean;
}

export interface UIState {
  showLabels: boolean;
  showEdges: boolean;
  showHelp: boolean;
  showDebug: boolean;
  sidebarOpen: boolean;
  notifications: Notification[];
}

export interface Notification {
  id: string;
  type: 'info' | 'warning' | 'critical' | 'success';
  title: string;
  message: string;
  timestamp: number;
  duration: number;
}

// ═══════════════════════════════════════════════════════════════════════════════
// CLASE STATE MANAGER
// ═══════════════════════════════════════════════════════════════════════════════

class AppState {
  // Paciente actual
  currentPatient: PatientGraph | null = null;
  
  // Topología
  topologyMode: TopologyMode = 'anatomical';
  topologyConfig: TopologyConfiguration = {
    mode: 'anatomical',
    centerPosition: new THREE.Vector3(0, 0, 0),
    scale: 1.0,
    timeScale: 60,
    showLabels: true,
    showRelationships: true,
  };
  
  // Manos
  leftHand: HandState = this.createEmptyHandState();
  rightHand: HandState = this.createEmptyHandState();
  
  // Simulación
  simulation: SimulationState = {
    isPaused: false,
    timeScale: 60,
    globalTime: 0,
    patientTime: 0,
    deltaTime: 0,
    frameCount: 0,
  };
  
  // Interacción
  interaction: InteractionState = {
    hoveredNodeId: null,
    selectedNodeId: null,
    draggedNodeId: null,
    lastInteractionTime: 0,
    isEmergencyMode: false,
  };
  
  // UI
  ui: UIState = {
    showLabels: true,
    showEdges: true,
    showHelp: false,
    showDebug: false,
    sidebarOpen: true,
    notifications: [],
  };
  
  // Diagnóstico
  detectedPatterns: DetectedPattern[] = [];
  differentialDiagnoses: DifferentialDiagnosis[] = [];
  leadingDiagnosis: DifferentialDiagnosis | null = null;
  
  // Eventos
  private eventListeners: Map<string, Set<(event: ClinicalEvent) => void>> = new Map();
  
  // Focus anatómico actual
  focusedSystem: AnatomicalSystem | null = null;

  // Modo educativo
  educationMode: 'none' | 'quiz' | 'timeline' | 'tutorial' = 'none';

  // Arbitraje de input: mano tiene prioridad mientras esté visible y
  // mantiene una "gracia" de 500ms tras perderse para evitar parpadeos
  // cuando el alumno baja la mano y vuelve a subirla.
  private lastHandActiveTime = 0;
  private static readonly HAND_GRACE_MS = 500;

  // Hipótesis diagnóstica actual (la que lidera o la que el alumno eligió)
  currentHypothesis: { caseId: string; dxName: string; dxCode: string; probability: number; confirmed: boolean } | null = null;

  // IDs de findings ya revelados (evita duplicados cuando un nodo se revela múltiples veces)
  revealedFindings: Set<string> = new Set();

  /**
   * Subconjunto de revealedFindings que se revelaron POR ACCIÓN del alumno
   * (click, examen, gesto, timeline), excluyendo los que se revelaron como
   * 'initial' al cargar el caso. Lo usa el AIAssistantPanel como gate
   * pedagógico: el alumno tiene que interactuar con el grafo antes de pedir IA.
   */
  revealedByAction: Set<string> = new Set();

  /**
   * Registra que el alumno seleccionó un diagnóstico en el DifferentialPanel.
   * Se setea la primera vez que el alumno intenta un dx (aunque sea incorrecto).
   * Sirve como gate pedagógico para el tab "Diagnóstico" del Asistente IA:
   * la IA no te da la respuesta hasta que vos hayas intentado.
   */
  userProposedDx: { name: string; correct: boolean; at: number } | null = null;

  /**
   * Triage que el alumno asignó al paciente (independiente del dx). Se
   * pide ANTES del DifferentialPanel, replicando el flujo real: triage
   * en ≤60s sin conocer dx, después razonamiento más profundo.
   */
  userProposedTriage: TriagePriority | null = null;

  /**
   * Flag para el rubric: se setea a true si el alumno abrió el tab Diagnóstico
   * del Asistente IA ANTES de proponer su propio dx. Penaliza el score final
   * (alumno prefirió consultar IA antes de razonar).
   */
  userOpenedAIBeforeDx = false;

  /**
   * ID de la asignación en curso, si el caso fue iniciado desde el panel
   * "Mis tareas" del alumno. Al completar el caso se hace `markDone` con
   * este id + score, y el profesor ve el resultado en su lista.
   */
  activeAssignmentId: number | null = null;

  // ═════════════════════════════════════════════════════════════════════════════
  // HAND STATE
  // ═════════════════════════════════════════════════════════════════════════════
  
  private createEmptyHandState(): HandState {
    return {
      isPresent: false,
      isPinching: false,
      isGrabbing: false,
      pinchDistance: 1,
      position: new THREE.Vector3(0, 0, 0),
      velocity: new THREE.Vector3(0, 0, 0),
      indexTipScreen: null,
      gesture: null,
      lastGestureTime: 0,
    };
  }

  updateHandState(
    hand: 'left' | 'right',
    updates: Partial<HandState>
  ): void {
    const handState = hand === 'left' ? this.leftHand : this.rightHand;
    Object.assign(handState, updates);
  }

  getActiveHand(): HandState | null {
    if (this.rightHand.isPresent) return this.rightHand;
    if (this.leftHand.isPresent) return this.leftHand;
    return null;
  }

  /**
   * Decide quién tiene el control del cursor/selección. La mano es dueña
   * mientras esté presente; tras perderla se respeta una gracia para que
   * un parpadeo de detección no devuelva el control al mouse.
   */
  inputOwner(): 'hand' | 'mouse' {
    if (this.rightHand.isPresent || this.leftHand.isPresent) {
      this.lastHandActiveTime = Date.now();
      return 'hand';
    }
    if (Date.now() - this.lastHandActiveTime < AppState.HAND_GRACE_MS) {
      return 'hand';
    }
    return 'mouse';
  }

  getBothHands(): [HandState | null, HandState | null] {
    const right = this.rightHand.isPresent ? this.rightHand : null;
    const left = this.leftHand.isPresent ? this.leftHand : null;
    return [right, left];
  }

  getHandsDistance(): number | null {
    if (!this.leftHand.isPresent || !this.rightHand.isPresent) return null;
    return this.leftHand.position.distanceTo(this.rightHand.position);
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // SIMULATION
  // ═════════════════════════════════════════════════════════════════════════════
  
  updateSimulation(deltaTime: number): void {
    if (this.simulation.isPaused) {
      this.simulation.deltaTime = 0;
      return;
    }

    this.simulation.deltaTime = deltaTime;
    this.simulation.globalTime += deltaTime;
    this.simulation.patientTime += deltaTime * this.simulation.timeScale;
    this.simulation.frameCount++;
  }

  setTimeScale(scale: number): void {
    // MEDICAL_PHYSICS.TIME_SCALE_MIN/MAX definen el rango válido del reloj
    // clínico (1s real = [1, 3600]s de paciente). El mínimo real es 1 (tiempo
    // real), no algo derivado de hand tracking.
    const clamped = Math.max(
      MEDICAL_PHYSICS.TIME_SCALE_MIN,
      Math.min(MEDICAL_PHYSICS.TIME_SCALE_MAX, scale)
    );
    this.simulation.timeScale = clamped;
    this.emitEvent({ type: 'TIME_SCALE_CHANGE', scale: clamped });
  }

  togglePause(): void {
    this.simulation.isPaused = !this.simulation.isPaused;
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // TOPOLOGY
  // ═════════════════════════════════════════════════════════════════════════════
  
  setTopologyMode(mode: TopologyMode): void {
    this.topologyMode = mode;
    this.topologyConfig.mode = mode;
    
    // Actualizar configuración específica del modo
    switch (mode) {
      case 'anatomical':
        this.topologyConfig.centerPosition.set(0, 0, 0);
        break;
      case 'temporal_spiral':
        this.topologyConfig.centerPosition.set(0, 0, 0);
        break;
      case 'differential_competitive':
        this.topologyConfig.centerPosition.set(0, 0, 0);
        break;
    }
    
    this.emitEvent({ type: 'TOPOLOGY_CHANGE', mode });
  }

  cycleTopologyMode(): void {
    const modes: TopologyMode[] = [
      'anatomical',
      'fisiopathological',
      'temporal_spiral',
      'differential_competitive',
    ];
    const currentIndex = modes.indexOf(this.topologyMode);
    const nextIndex = (currentIndex + 1) % modes.length;
    this.setTopologyMode(modes[nextIndex]);
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // INTERACTION
  // ═════════════════════════════════════════════════════════════════════════════
  
  setHoveredNode(nodeId: string | null): void {
    if (this.interaction.hoveredNodeId !== nodeId) {
      this.interaction.hoveredNodeId = nodeId;
      if (nodeId) {
        this.emitEvent({ type: 'NODE_HOVER', nodeId });
      }
    }
  }

  setSelectedNode(nodeId: string | null): void {
    const previousId = this.interaction.selectedNodeId;
    this.interaction.selectedNodeId = nodeId;
    this.interaction.lastInteractionTime = Date.now();

    // Clase en body para que el CSS baje la opacidad de las labels vecinas
    // y el foco visual quede claro mientras hay un nodo seleccionado.
    if (typeof document !== 'undefined') {
      document.body.classList.toggle('cgt-has-focused-label', nodeId !== null);
    }

    if (nodeId && nodeId !== previousId) {
      this.emitEvent({ type: 'NODE_SELECT', nodeId });
    }
  }

  setDraggedNode(nodeId: string | null): void {
    this.interaction.draggedNodeId = nodeId;
  }

  toggleEmergencyMode(): void {
    this.interaction.isEmergencyMode = !this.interaction.isEmergencyMode;
    this.emitEvent({ 
      type: 'EMERGENCY_MODE', 
      active: this.interaction.isEmergencyMode 
    });
    
    if (this.interaction.isEmergencyMode) {
      this.addNotification({
        type: 'critical',
        title: 'MODO EMERGENCIA ACTIVADO',
        message: 'Mostrando solo signos vitales críticos (ABC)',
        duration: 5000,
      });
    }
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // PATIENT
  // ═════════════════════════════════════════════════════════════════════════════
  
  setPatient(patient: PatientGraph): void {
    this.currentPatient = patient;
    this.detectedPatterns = [];
    this.differentialDiagnoses = [];
    this.leadingDiagnosis = null;
    this.currentHypothesis = null;
    this.revealedFindings = new Set();
    this.revealedByAction = new Set();
    this.userProposedDx = null;
    this.userProposedTriage = null;
    this.userOpenedAIBeforeDx = false;
    // NO limpiamos activeAssignmentId acá: el setPatient corre cuando
    // AssignmentsPanel carga el caso, y necesita preservarlo entre clicks.
    this.interaction.isEmergencyMode = false;

    // El reloj del paciente arranca en t=0 con cada caso. Sin esto, el
    // DeteriorationEngine dispara todos los milestones de golpe cuando
    // el patientTime acumulado de la sesión ya supera cualquier trayectoria.
    this.simulation.patientTime = 0;
    this.simulation.frameCount = 0;

    this.addNotification({
      type: 'info',
      title: 'Paciente cargado',
      message: `ID: ${patient.id} - Prioridad: ${patient.triagePriority}`,
      duration: 3000,
    });
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // BUS DE EVENTOS CLÍNICOS
  // ═════════════════════════════════════════════════════════════════════════════

  /**
   * Marca un nodo como "revelado al alumno" y notifica al resto del sistema.
   * Idempotente: llamarlo dos veces con el mismo nodeId no dispara dos eventos.
   * Los listeners (ReasoningEngine, DifferentialPanel, etc.) reaccionan en cascada.
   */
  emitFindingRevealed(nodeId: string, source: import('../types/medical').FindingSource = 'click'): void {
    // El evento FINDING_REVEALED se emite sólo la primera vez que un nodo
    // se revela. Pero la señal "el alumno tocó este nodo" (revealedByAction)
    // es independiente: cuenta aunque el nodo ya estuviera visible desde el
    // inicio — si no, en modo exploración libre (discovery=false) todos los
    // nodos arrancan como 'initial' y el gate nunca se destrancaría.
    const firstReveal = !this.revealedFindings.has(nodeId);
    if (firstReveal) {
      this.revealedFindings.add(nodeId);
      this.emitEvent({ type: 'FINDING_REVEALED', nodeId, source });
    }
    if (source !== 'initial') this.revealedByAction.add(nodeId);
  }

  /**
   * Reporta que el alumno ordenó un examen (incluye los nodos que se revelarán).
   * Se emite ANTES de los FINDING_REVEALED individuales para que listeners
   * puedan agrupar la reacción (ej. mostrar "procesando…").
   */
  emitExamOrdered(examId: string, examLabel: string, nodeIds: string[]): void {
    this.emitEvent({ type: 'EXAM_ORDERED', examId, examLabel, nodeIds });
  }

  /**
   * Guarda la hipótesis diagnóstica actual. `confirmed=false` significa
   * "es el líder según las probabilidades", `confirmed=true` significa
   * "el alumno la eligió explícitamente". Emite HYPOTHESIS_SET solo si cambia.
   */
  setHypothesis(caseId: string, dxName: string, dxCode: string, probability: number, confirmed: boolean): void {
    const prev = this.currentHypothesis;
    if (prev && prev.dxName === dxName && prev.confirmed === confirmed && Math.abs(prev.probability - probability) < 1) {
      return;
    }
    this.currentHypothesis = { caseId, dxName, dxCode, probability, confirmed };
    this.emitEvent({ type: 'HYPOTHESIS_SET', caseId, dxName, dxCode, probability, confirmed });
  }

  updatePatientPriority(newPriority: TriagePriority): void {
    if (!this.currentPatient) return;
    
    const oldPriority = this.currentPatient.triagePriority;
    this.currentPatient.triagePriority = newPriority;
    
    this.emitEvent({
      type: 'TRIAGE_CHANGE',
      from: oldPriority,
      to: newPriority,
    });
    
    if (newPriority === 'immediate' && oldPriority !== 'immediate') {
      this.addNotification({
        type: 'critical',
        title: '⚠️ PRIORIDAD CRÍTICA',
        message: 'El paciente requiere atención inmediata',
        duration: 0, // Persistente
      });
    }
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // DIAGNÓSTICO
  // ═════════════════════════════════════════════════════════════════════════════
  
  addDetectedPattern(pattern: DetectedPattern): void {
    const exists = this.detectedPatterns.find(p => p.pattern.id === pattern.pattern.id);
    if (!exists) {
      this.detectedPatterns.push(pattern);
      this.emitEvent({ type: 'PATTERN_DETECTED', pattern });
      
      this.addNotification({
        type: 'warning',
        title: 'Patrón Clínico Detectado',
        message: `${pattern.pattern.name}: ${pattern.pattern.condition}`,
        duration: 8000,
      });
    }
  }

  updateDifferentialDiagnoses(diagnoses: DifferentialDiagnosis[]): void {
    this.differentialDiagnoses = diagnoses;
    
    const newLeading = diagnoses.length > 0 ? diagnoses[0] : null;
    if (newLeading?.concept.code !== this.leadingDiagnosis?.concept.code) {
      this.leadingDiagnosis = newLeading;
      if (newLeading) {
        this.emitEvent({ type: 'DIAGNOSIS_LEAD_CHANGE', diagnosis: newLeading });
      }
    }
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // NOTIFICATIONS
  // ═════════════════════════════════════════════════════════════════════════════
  
  addNotification(notification: Omit<Notification, 'id' | 'timestamp'>): void {
    const newNotification: Notification = {
      ...notification,
      id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
    };
    
    this.ui.notifications.unshift(newNotification);
    
    // Limitar número de notificaciones
    if (this.ui.notifications.length > 10) {
      this.ui.notifications = this.ui.notifications.slice(0, 10);
    }
    
    // Auto-remove si tiene duración
    if (notification.duration > 0) {
      setTimeout(() => {
        this.removeNotification(newNotification.id);
      }, notification.duration);
    }
  }

  removeNotification(id: string): void {
    const index = this.ui.notifications.findIndex(n => n.id === id);
    if (index !== -1) {
      this.ui.notifications.splice(index, 1);
    }
  }

  clearNotifications(): void {
    this.ui.notifications = [];
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // EVENTS
  // ═════════════════════════════════════════════════════════════════════════════
  
  onEvent(
    type: ClinicalEvent['type'],
    callback: (event: ClinicalEvent) => void
  ): () => void {
    if (!this.eventListeners.has(type)) {
      this.eventListeners.set(type, new Set());
    }
    this.eventListeners.get(type)!.add(callback);
    
    // Return unsubscribe function
    return () => {
      this.eventListeners.get(type)?.delete(callback);
    };
  }

  emit(event: ClinicalEvent): void {
    this.emitEvent(event);
  }

  setEducationMode(mode: 'none' | 'quiz' | 'timeline' | 'tutorial'): void {
    this.educationMode = mode;
  }

  private emitEvent(event: ClinicalEvent): void {
    const listeners = this.eventListeners.get(event.type);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(event);
        } catch (error) {
          console.error('Error in event listener:', error);
        }
      });
    }
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // UI
  // ═════════════════════════════════════════════════════════════════════════════
  
  toggleLabels(): void {
    this.ui.showLabels = !this.ui.showLabels;
  }

  toggleEdges(): void {
    this.ui.showEdges = !this.ui.showEdges;
  }

  toggleHelp(): void {
    this.ui.showHelp = !this.ui.showHelp;
  }

  toggleDebug(): void {
    this.ui.showDebug = !this.ui.showDebug;
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // RESET
  // ═════════════════════════════════════════════════════════════════════════════
  
  reset(): void {
    this.currentPatient = null;
    this.detectedPatterns = [];
    this.differentialDiagnoses = [];
    this.leadingDiagnosis = null;
    this.currentHypothesis = null;
    this.revealedFindings = new Set();
    this.revealedByAction = new Set();
    this.userProposedDx = null;
    this.userProposedTriage = null;
    this.userOpenedAIBeforeDx = false;
    this.activeAssignmentId = null;
    this.interaction = {
      hoveredNodeId: null,
      selectedNodeId: null,
      draggedNodeId: null,
      lastInteractionTime: 0,
      isEmergencyMode: false,
    };
    this.simulation = {
      isPaused: false,
      timeScale: 60,
      globalTime: 0,
      patientTime: 0,
      deltaTime: 0,
      frameCount: 0,
    };
    this.ui.notifications = [];
  }
}

// Export singleton instance
export const state = new AppState();
export default state;
